// ---------------------------------------------------------------------------
// The browser side of THE COMPLIANCE CENTRE. V30.
//
// NO VOCABULARY IS SPELLED HERE OR IN THE SCREEN. Every system arrives as an id
// AND its label AND the sentence a worker reads; every prohibited class arrives
// with its own label; every free-text column arrives with the label of its
// content kind and of its author kind, and with `is_scanned` already decided by
// the database. `verify-V30.sh` reads all four vocabularies out of the database
// and greps this directory for the quoted form of every value.
//
// THE SCREEN DECIDES NOTHING ABOUT WHAT IS COMPLIANT. Whether a system explains
// itself, whether it affects a worker, and whether it has a review route are all
// facts the register holds and the migration asserted against the catalogue. A
// browser that inferred any of them would be a second register, written where
// nobody checks it — which is the exact failure this slice exists to prevent.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type AutomatedSystem = {
  system_id: string;
  label: string;
  rank: number;
  /** Which slice built it. On the screen, because a claim with no provenance is
   *  a claim nobody can check. */
  introduced_slice: string;
  /** The plain sentence. Directive 2024/2831 asks for the worker to be INFORMED,
   *  and a register written in the vocabulary of the schema informs nobody. */
  purpose: string;
  is_automated_decision: boolean;
  affects_worker: boolean;
  explanation_kind: string;
  /** S70 (0203). How it explains itself, worded by its kind's row. */
  explanation_sentence: string;
  review_route_kind: string | null;
  review_route: string | null;
  /** The permission the route asks for. The screen names the humans who hold it. */
  review_permission: string | null;
  /** Tour T-40: the labels of the roles that hold that permission, in rank order. */
  review_roles?: string[];
  no_review_route_reason: string | null;
  no_review_reason_label: string | null;
  inputs: string[];
};

export type FreeTextField = {
  relation: string;
  attribute: string;
  content_kind: string;
  content_label: string;
  /** Whether the prohibited-data guard is installed over it. Decided by the
   *  content kind in the database, never by this file. */
  is_scanned: boolean;
  author_kind: string;
  author_label: string;
};

export type ProhibitedClass = {
  class_id: string;
  class_label: string;
  description: string;
  pattern_count: number;
};

export type ComplianceRegister = {
  systems: AutomatedSystem[];
  free_text_fields: FreeTextField[];
  prohibited_classes: ProhibitedClass[];
  scanned_field_count: number;
  worker_affecting_count: number;
};

export type ReviewQueueRow = {
  system_id: string;
  system_label: string;
  subject_kind: string;
  subject_id: string;
  opened_at: string;
  is_decided: boolean;
  decided_at: string | null;
  outcome_label: string | null;
};

export type Reviewer = { user_account_id: string; reviewer_email: string; role_id: string };

export type ComplianceReviews = {
  queue: ReviewQueueRow[];
  /** May be empty, and an empty list is a FINDING rather than a blank: nobody
   *  holds the permission, so this organisation has no review route in practice
   *  whatever the register says. */
  reviewers: Reviewer[];
  open_count: number;
};

/** Postgres `numeric` arrives as a STRING, and is left as one: parsing it here
 *  to print it would be the float this product refuses everywhere else. */
export type WorkingTimeLimit = {
  country: string;
  max_weekly_hours: string;
  max_daily_hours: string;
  reference_period_days: number;
  min_daily_rest_hours: string;
  min_weekly_rest_hours: string;
  max_consecutive_days: number;
  source: string;
};

export type WorkingTimeAgreement = {
  agreement_reference: string;
  agreed_on: string;
  recorded_at: string;
};

/** BOTH LEVELS, because one without the other cannot answer "is 40 hours the law
 *  or our own agreement?" — the only question this panel is asked. */
export type WorkingTimeView = {
  resolved: WorkingTimeLimit | null;
  baseline: WorkingTimeLimit | null;
  agreement: WorkingTimeAgreement | null;
};

export type WorkingTimeDraft = {
  agreement_reference: string;
  agreed_on: string;
  max_weekly_hours?: number;
  max_daily_hours?: number;
  reference_period_days?: number;
  min_daily_rest_hours?: number;
  min_weekly_rest_hours?: number;
  max_consecutive_days?: number;
};

/** S0-176 (0312). A documented exception to a legal working-time limit. */
export type WorkingTimeException = {
  id: string;
  worker_id: string;
  worker_name: string;
  kind: string;
  kind_label: string;
  valid_from: string;
  valid_until: string;
  legal_ground: string;
  supporting_agreement: string;
  approved_at: string;
  approved_by: string | null;
  revoked_at: string | null;
};

export type WorkingTimeExceptions = {
  exceptions: WorkingTimeException[];
  kinds: { id: string; label: string }[];
};

export type WorkingTimeExceptionDraft = {
  worker_id: string;
  kind: string;
  valid_from: string;
  valid_until: string;
  legal_ground: string;
  supporting_agreement: string;
};

export const compliance = {
  register: (base: string) => read(base, "/v1/compliance/register"),
  reviews: (base: string) => read(base, "/v1/compliance/reviews"),
  workingTime: (base: string) => read(base, "/v1/compliance/working-time"),
  recordWorkingTime: (base: string, payload: WorkingTimeDraft) =>
    mutate("POST", base, "/v1/compliance/working-time", payload),
  workingTimeExceptions: (base: string) => read(base, "/v1/compliance/working-time-exceptions"),
  geofenceDpia: (base: string) => read(base, "/v1/compliance/geofence-dpia"),
  recordGeofenceDpia: (base: string, payload: { reference: string; completed_on: string }) =>
    mutate("POST", base, "/v1/compliance/geofence-dpia", payload),
  recordWorkingTimeException: (base: string, payload: WorkingTimeExceptionDraft) =>
    mutate("POST", base, "/v1/compliance/working-time-exceptions", payload),
  revokeWorkingTimeException: (base: string, id: string) =>
    mutate("POST", base, `/v1/compliance/working-time-exceptions/${encodeURIComponent(id)}/revoke`, {}),
};
